import { CrudCollection } from "../CrudCollection";

export function readCollectionQueryState(
  collection: CrudCollection
): Record<string, string> {
  // Scope, not state (asana-995, Task 5): a static filter (e.g. My Cases'
  // `manager`) exists to scope the list to what the signed-in user is
  // allowed to see, not to describe a user's chosen view. A saved set or a
  // sticky blob must never carry it -- carrying it would hand one user's
  // scope to whoever loads the set (a Shared filter set, in particular).
  const staticQueryKeys = new Set(
    collection.staticFilters.map((filter) => filter.queryName)
  );

  const state = Object.entries(
    collection.remoteQueryValues as Record<string, any>
  ).reduce(
    (state, [key, value]) => {
      if (
        key === "_page" ||
        staticQueryKeys.has(key) ||
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return state;
      }

      state[key] = Array.isArray(value)
        ? value.map(String).join(",")
        : String(value);

      return state;
    },
    {} as Record<string, string>
  );

  // The export column selection isn't part of a list fetch, so it isn't in
  // remoteQueryValues -- append it under its own reserved keys. Neither key
  // is written when the selection is empty, so an untouched collection
  // still serializes to the same bag it always has.
  if (collection.fullExport) {
    state._exportAll = "1";
  } else if (collection.columns && collection.columns.length) {
    state._exportColumns = collection.columns.join(",");
  }

  return state;
}

export function applyCollectionQueryState(
  collection: CrudCollection,
  state: Record<string, any>
): void {
  // Replace, not append (asana-995, Task 5): the incoming state describes
  // the WHOLE non-static filter picture -- a key present sets that filter,
  // and a key absent clears it, so a saved set that omits a filter actually
  // removes it rather than leaving it standing. Static filters (scope, not
  // state -- e.g. My Cases' `manager`) are exempt: nothing here may clear
  // one, restored or not.
  [...collection.remoteQueryFilters, collection.dynamicFilterController]
    .filter((filter) => !filter.isStatic)
    .forEach((filter) => {
      if (Object.prototype.hasOwnProperty.call(state, filter.name)) {
        collection.setFilter(filter.name, state[filter.name]);
      } else {
        filter.clear();
      }
    });

  // Reserved keys, handled explicitly rather than falling into the generic
  // `_`-prefixed loop below -- they aren't remoteQueryOptions. An absent key
  // means "no saved selection", restoring the default-export behaviour.
  collection.setExportState({
    columns:
      typeof state._exportColumns === "string" && state._exportColumns.length
        ? state._exportColumns.split(",")
        : null,
    fullExport: !!state._exportAll,
  });

  Object.keys(state)
    .filter((key) => key.startsWith("_"))
    .filter((key) => key !== "_exportAll" && key !== "_exportColumns")
    .map((key) => key.substring(1))
    .filter((queryOption) =>
      collection.remoteQueryOptions.hasOwnProperty(queryOption)
    )
    .forEach((queryOption) => {
      const key = `_${queryOption}`;
      const rawValue = state[key];
      let optionValue = rawValue;

      if (queryOption.startsWith("sort")) {
        const serializedValue = Array.isArray(rawValue)
          ? rawValue.join(",")
          : String(rawValue);
        optionValue =
          serializedValue === "" ? [] : serializedValue.split(",");

        if (queryOption === "sortDesc") {
          optionValue = optionValue.map(Number);
        }
      }

      collection.setQueryOption(
        queryOption as keyof typeof collection.remoteQueryOptions,
        optionValue
      );
    });

  collection.applyStagedFilters();
}
